// src/components/viewer/InfoCard.jsx

import React from 'react';

const InfoCard = ({ data }) => {
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleString();
        } catch (e) {
            return dateString;
        }
    };

    // Helper: format array fields
    const formatArray = (arr) => {
        if (!arr || arr.length === 0) return null;
        return Array.isArray(arr) ? arr.join(', ') : arr;
    };

    // Helper: format frequency values to readable labels
    const formatFrequency = (arr) => {
        if (!arr || arr.length === 0) return null;
        const labels = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
        const formatted = (Array.isArray(arr) ? arr : [arr]).map(v => labels[v] || v);
        return formatted.join(', ');
    };

    // Helper: format search criteria values to readable labels
    const formatSearchCriteria = (arr) => {
        if (!arr || arr.length === 0) return null;
        const labels = {
            seller_id: 'Seller ID', email: 'Email', phone: 'Phone',
            safet_claim_id: 'SAFE-T Claim ID', order_id: 'Order ID',
            investigation_id: 'Investigation ID', credit_card_id: 'Credit Card ID',
            customer_name: 'Customer Name', contact_id: 'Contact ID',
            atoz_claim_id: 'A-to-Z Claim ID'
        };
        const formatted = (Array.isArray(arr) ? arr : [arr]).map(v => labels[v] || v);
        return formatted.join(', ');
    };

    // Determine team display (custom team name if "Others")
    const teamDisplay = data.customTeamName 
        ? `${data.team || 'Others'} — ${data.customTeamName}` 
        : (data.team || 'N/A');

    // Determine request type display
    const getRequestTypeLabel = (type) => {
        if (type === 'new') return '🆕 UFS Request: Approved Use Case';
        if (type === 'new_unlisted') return '📋 UFS Request: Unlisted Use Case';
        if (type === 'renewal') return '🔄 Renewal Request';
        return type || 'N/A';
    };

    return (
        <div className="info-card">
            <div className="info-grid">
                {/* ── Existing fields ── */}
                {data.requestType && (
                    <div className="info-item">
                        <label>Request Type</label>
                        <span className="value">{getRequestTypeLabel(data.requestType)}</span>
                    </div>
                )}
                <div className="info-item">
                    <label>Function</label>
                    <span className="value">{data.functionDropdown || 'N/A'}</span>
                </div>

                {/* 🆕 Org Name */}
                {data.orgName && (
                    <div className="info-item">
                        <label>Org Name</label>
                        <span className="value">{data.orgName}</span>
                    </div>
                )}

                <div className="info-item">
                    <label>Team</label>
                    <span className="value">{teamDisplay}</span>
                </div>
                <div className="info-item">
                    <label>Use Case</label>
                    <span className="value">{data.useCase || 'N/A'}</span>
                </div>
                <div className="info-item">
                    <label>Priority</label>
                    <span className="value">{data.priority || 'Medium'}</span>
                </div>

                {/* 🆕 User Levels */}
                {formatArray(data.userLevels) && (
                    <div className="info-item">
                        <label>User Levels</label>
                        <span className="value viewer-tags">
                            {(Array.isArray(data.userLevels) ? data.userLevels : []).map((level) => (
                                <span key={level} className="viewer-tag">{level}</span>
                            ))}
                        </span>
                    </div>
                )}

                {/* 🆕 Frequency */}
                {formatFrequency(data.frequency) && (
                    <div className="info-item">
                        <label>Access Frequency</label>
                        <span className="value viewer-tags">
                            {(Array.isArray(data.frequency) ? data.frequency : []).map((freq) => (
                                <span key={freq} className="viewer-tag">
                                    {freq === 'daily' ? '📅 Daily' : freq === 'weekly' ? '📆 Weekly' : freq === 'monthly' ? '🗓️ Monthly' : freq}
                                </span>
                            ))}
                        </span>
                    </div>
                )}

                {/* 🆕 Search Criteria */}
                {formatSearchCriteria(data.searchCriteria) && (
                    <div className="info-item">
                        <label>UFS Search Criteria</label>
                        <span className="value viewer-tags">
                            {(Array.isArray(data.searchCriteria) ? data.searchCriteria : []).map((criteria) => {
                                const labels = {
                                    seller_id: 'Seller ID', email: 'Email', phone: 'Phone',
                                    safet_claim_id: 'SAFE-T Claim ID', order_id: 'Order ID',
                                    investigation_id: 'Investigation ID', credit_card_id: 'Credit Card ID',
                                    customer_name: 'Customer Name', contact_id: 'Contact ID',
                                    atoz_claim_id: 'A-to-Z Claim ID'
                                };
                                return <span key={criteria} className="viewer-tag">{labels[criteria] || criteria}</span>;
                            })}
                        </span>
                    </div>
                )}

                {/* 🆕 Queues */}
                {data.queues && (
                    <div className="info-item info-item-full">
                        <label>UFS Queue List</label>
                        <span className="value viewer-queues">{data.queues}</span>
                    </div>
                )}

                <div className="info-item info-item-full">
                    <label>Justification</label>
                    <span className="value">{data.justification || 'N/A'}</span>
                </div>
                <div className="info-item">
                    <label>Submitted</label>
                    <span className="value">{formatDate(data.createdAt)}</span>
                </div>
            </div>
        </div>
    );
};

export default InfoCard;